<template>
    <div id="zaui_page">
        <div v-if="!isOfficeAdmin" class="restricted-message">
            This page is restricted to Office Admins.
        </div>
        <template v-else>
            <h1>Zaui Sync</h1>

            <!-- Priority 1, G1: the admin-facing feature - pull one day's manifest on
                 demand, with real progress feedback and a readable result instead of a
                 raw v-html dump. -->
            <div class="section-card">
                <h2>Pull Manifest for a Date</h2>
                <p class="section-intro">Manually fetch and sync a day's manifest from Zaui right now, instead of waiting for the scheduled auto-sync.</p>
                <div class="pull-controls">
                    <label>Date:</label>
                    <DatePicker v-model="selectedDate" @change="handleDateChange" />
                    <button class="primary-button" @click="pullManifest" :disabled="pullLoading">
                        {{ pullLoading ? 'Pulling...' : 'Pull Manifest' }}
                    </button>
                </div>

                <div v-if="pullLoading" class="progress-message">
                    <img class="loading-gif" src="@/assets/hamster.gif" alt="Loading..." />
                    <p>Fetching from Zaui and syncing guests, reservations, and custom fields - this can take a little while for a busy day...</p>
                </div>

                <div v-else-if="pullResult" class="pull-result" :class="{ 'pull-result-error': pullResult.error }">
                    <p v-if="pullResult.error">{{ pullResult.error }}</p>
                    <template v-else>
                        <p><strong>{{ pullResult.total }}</strong> booking(s) processed
                            <span v-if="pullResult.errorCount">, <strong>{{ pullResult.errorCount }}</strong> with errors</span>.
                        </p>
                        <ul v-if="pullResult.people.length" class="pull-result-list">
                            <li v-for="(p, i) in pullResult.people" :key="i">
                                {{ p.name }} <small v-if="p.groupcode">· {{ p.groupcode }}</small>
                            </li>
                        </ul>
                        <ul v-if="pullResult.errors.length" class="pull-result-list pull-result-errors">
                            <li v-for="(e, i) in pullResult.errors" :key="'err' + i">{{ e }}</li>
                        </ul>
                    </template>
                </div>
            </div>

            <!-- Priority 1, G2: auto-sync on/off + schedule, was hardcoded in
                 dailymanifestcron.js before this. Saving reschedules the live cron
                 jobs immediately (see rescheduleManifestFetch server-side) - no
                 restart needed. -->
            <div class="section-card">
                <h2>Auto-Sync Settings</h2>
                <div v-if="settingsLoading" class="progress-message"><p>Loading settings...</p></div>
                <template v-else-if="settings">
                    <label class="settings-toggle">
                        <input type="checkbox" v-model="settings.enabled">
                        Auto-sync enabled
                    </label>

                    <div class="settings-grid">
                        <div class="settings-row">
                            <label>Morning run</label>
                            <input type="time" v-model="settings.morning_time">
                            <span>+</span>
                            <input type="number" min="0" max="30" v-model.number="settings.morning_days_ahead" class="days-input">
                            <span>day(s) ahead</span>
                        </div>
                        <div class="settings-row">
                            <label>Afternoon run</label>
                            <input type="time" v-model="settings.evening_time">
                            <span>+</span>
                            <input type="number" min="0" max="30" v-model.number="settings.evening_days_ahead" class="days-input">
                            <span>day(s) ahead</span>
                        </div>
                        <div class="settings-row">
                            <label>Timezone</label>
                            <input type="text" v-model="settings.timezone">
                        </div>
                    </div>

                    <button class="primary-button" @click="saveSettings" :disabled="settingsSaving">
                        {{ settingsSaving ? 'Saving...' : 'Save Settings' }}
                    </button>
                    <span v-if="settingsSaved" class="saved-message">Saved - schedule updated.</span>
                </template>
            </div>

            <!-- The original raw test buttons - still useful for debugging, just not
                 the primary thing an Office Admin opens this page to do. -->
            <div class="collapsible-section">
                <div class="header" @click="isDevToolsCollapsed = !isDevToolsCollapsed">
                    <h2>Developer Tools</h2>
                    <span>{{ isDevToolsCollapsed ? '▼' : '▲' }}</span>
                </div>
                <div v-if="!isDevToolsCollapsed" class="content">
                    <div style="margin-bottom: 12px; padding-bottom: 12px">
                        <button style="margin-right: 20px;" @click="getZauiPing">Ping Zaui</button>
                        <button @click="getBookingByDay">Get All Bookings Object</button>
                        <button @click="testZauiMapping">Test Zaui Mapping (for date above)</button>
                    </div>
                    <div style="margin-bottom: 12px; padding-bottom: 12px">
                        <button @click="getGuestProfile">Test Get Guest Profile</button>
                    </div>
                    <div style="margin-bottom: 12px; padding-bottom: 12px">
                        <label for="bookingNumber">Booking Number:</label>
                        <input type="text" id="bookingNumber" v-model="bookingNumber">
                        <button @click="getBookingByNumber">Get Booking By Number</button>
                    </div>
                    <div v-if="isLoading" style="text-align: center; margin-top: 30px;">
                        <img class="loading-gif" src="@/assets/hamster.gif" alt="Loading..." />
                        <p>loading, please be patient</p>
                    </div>
                    <div v-else id="zaui_data_box" v-html="zaui_object_data"></div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import ZauiDataService from "../services/ZauiDataService";
import DatePicker from "./Shared/DatePicker.vue";
import { todayLocalDateString } from "@/utils/dateHelpers";

export default {
    name: "zaui",
    components: { DatePicker },
    data() {
        return {
            submitted: false,
            zaui_object_data: "gathered data will show up here...",
            selectedDate: this.getStoredDate() || todayLocalDateString(), // Default to today's date
            bookingNumber: '', // Add bookingNumber to the data properties
            isLoading: false, // Add isLoading to track loading state
            isDevToolsCollapsed: true,
            pullLoading: false,
            pullResult: null,
            settings: null,
            settingsLoading: true,
            settingsSaving: false,
            settingsSaved: false,
        };
    },
    computed: {
        isOfficeAdmin() {
            return sessionStorage.getItem('role') === 'admin';
        },
    },
    methods: {
        getStoredDate() {
            return sessionStorage.getItem('selectedDate');
        },
        storeDate(date) {
            sessionStorage.setItem('selectedDate', date);
        },
        handleDateChange() {
            this.storeDate(this.selectedDate);
        },
        async pullManifest() {
            this.pullLoading = true;
            this.pullResult = null;
            try {
                const response = await ZauiDataService.getManifestByDay(new Date(this.selectedDate));
                const data = response.data;

                if (!Array.isArray(data) || (data.length === 1 && data[0] === 'error')) {
                    this.pullResult = { error: 'No activities found for this date.' };
                    return;
                }

                const people = [];
                const errors = [];
                for (const item of data) {
                    if (item && item.person) {
                        const name = `${item.person.firstname || ''} ${item.person.lastname || ''}`.trim() || `personid ${item.person.personid}`;
                        people.push({ name, groupcode: item.reservation ? item.reservation.groupcode : null });
                    } else {
                        errors.push(item && item.message ? item.message : JSON.stringify(item));
                    }
                }

                this.pullResult = { total: data.length, people, errors, errorCount: errors.length };
            } catch (e) {
                console.error("Error pulling manifest:", e);
                this.pullResult = { error: "Failed to pull the manifest: " + (e.response?.data?.error || e.message) };
            } finally {
                this.pullLoading = false;
            }
        },
        async loadSyncSettings() {
            this.settingsLoading = true;
            try {
                const response = await ZauiDataService.getSyncSettings();
                this.settings = response.data;
            } catch (e) {
                console.error("Error loading sync settings:", e);
            } finally {
                this.settingsLoading = false;
            }
        },
        async saveSettings() {
            this.settingsSaving = true;
            this.settingsSaved = false;
            try {
                const response = await ZauiDataService.updateSyncSettings(this.settings);
                this.settings = response.data;
                this.settingsSaved = true;
                setTimeout(() => { this.settingsSaved = false; }, 3000);
            } catch (e) {
                console.error("Error saving sync settings:", e);
                alert("Failed to save settings: " + (e.response?.data?.message || e.message));
            } finally {
                this.settingsSaving = false;
            }
        },
        getZauiPing() {
            this.isLoading = true;
            ZauiDataService.ping()
                .then(response => {
                    this.zaui_object_data = response.data;
                    console.log(response.data);
                })
                .catch(e => {
                    console.log(e);
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        testZauiMapping() {
            this.isLoading = true;
            ZauiDataService.checkZauiMapping(new Date(this.selectedDate))
                .then(response => {
                    if (response.data.length === 1 && response.data[0].startsWith("No")) {
                        this.zaui_object_data = `<div style="background-color: red; color: white; padding: 10px;">${response.data[0]}</div>`;
                    } else {
                        let formattedData = "<ul style='list-style-type: none; padding: 0;'>";
                        response.data.forEach(item => {
                            const parts = item.split(" ");
                            const bookingNumber = parts[3];
                            const reservationId = parts[parts.length - 1];
                            const isReservationFound = !item.includes("No matching reservation found");
                            const backgroundColor = isReservationFound ? "lightgreen" : "lightcoral";
                            const textColor = "black";
                            const message = isReservationFound
                                            ? `Booking Number: <strong>${bookingNumber}</strong> - Reservation ID: <strong>${reservationId}</strong>`
                                            : `Booking Number: <strong>${bookingNumber}</strong> - No matching reservation found`;
                            formattedData += `<li style="background-color: ${backgroundColor}; color: ${textColor}; padding: 5px; margin-bottom: 2px;">${message}</li>`;
                        });
                        formattedData += "</ul>";
                        this.zaui_object_data = formattedData;
                    }
                    console.log(response.data);
                })
                .catch(e => {
                    console.log(e);
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        getBookingByDay() {
            this.isLoading = true;
            ZauiDataService.getBookingByDay(new Date(this.selectedDate))
                .then(response => {
                    this.zaui_object_data = response.data;
                })
                .catch(e => {
                    console.log(e);
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        getGuestProfile() {
            this.isLoading = true;
            const booking = {
                bookingNumber: "16322",
                guestFirstName: "Colin",
                guestLastName: "Wiseman",
                mobile: "4165602257",
                email: "wisecolin21@gmail.com"
            };
            ZauiDataService.getGuestProfile(booking)
                .then(response => {
                    this.zaui_object_data = response;
                })
                .catch(error => {
                    console.error("Error getting guest profile:", error);
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        getBookingByNumber() {
            this.isLoading = true;
            ZauiDataService.getBookingByNumber(this.bookingNumber)
                .then(response => {
                    this.zaui_object_data = JSON.stringify(response.data, null, 2); // Pretty print JSON
                })
                .catch(e => {
                    console.log(e);
                })
                .finally(() => {
                    this.isLoading = false;
                });
        }
    },
    mounted() {
        this.storeDate(this.selectedDate);
        if (this.isOfficeAdmin) {
            this.loadSyncSettings();
        }
    }
}
</script>

<style scoped>
.restricted-message {
    padding: 20px;
    background-color: #f8d7da;
    color: #721c24;
    border-radius: 5px;
}

.section-card {
    border: 1px solid #ccc;
    border-radius: 5px;
    padding: 15px;
    margin-bottom: 20px;
}

.section-intro {
    color: #666;
    margin-bottom: 12px;
}

.pull-controls {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
}

.primary-button {
    background-color: #0d6efd;
    color: white;
    border: none;
    padding: 6px 16px;
    border-radius: 4px;
    cursor: pointer;
}

.primary-button:disabled {
    background-color: #99c1fb;
    cursor: not-allowed;
}

.progress-message {
    text-align: center;
    padding: 10px;
}

.pull-result {
    background-color: #f8f9fa;
    border-radius: 4px;
    padding: 10px 15px;
}

.pull-result-error {
    background-color: #f8d7da;
    color: #721c24;
}

.pull-result-list {
    list-style: none;
    padding: 0;
    max-height: 300px;
    overflow-y: auto;
}

.pull-result-list li {
    padding: 4px 0;
    border-bottom: 1px solid #e9ecef;
}

.pull-result-errors li {
    color: #a94442;
}

.settings-toggle {
    display: block;
    margin-bottom: 12px;
    font-weight: bold;
}

.settings-grid {
    margin-bottom: 15px;
}

.settings-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
}

.settings-row label {
    width: 120px;
}

.days-input {
    width: 60px;
}

.saved-message {
    margin-left: 10px;
    color: #28a745;
}

.collapsible-section {
    border: 1px solid #ccc;
    border-radius: 5px;
    overflow: hidden;
}

.collapsible-section .header {
    background-color: #f5f5f5;
    padding: 10px;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.collapsible-section .content {
    padding: 15px;
}

#zaui_data_box {
    width: 100%;
    margin-top: 15px;
    border: 1px;
    border-color: black;
    border-style: dashed;
}

/* Force GIF animation to play in Chrome */
.loading-gif {
    width: 50px;
    height: 50px;
    display: inline-block;
}
</style>
